import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  CloudUpload,
  Assessment,
  CheckCircle,
  Error,
  Pending,
  Refresh,
} from '@mui/icons-material';
import {
  importPandoraData,
  getImportLogs,
  getStatistics,
  type ImportLog,
  type Statistics,
} from '../../services/jurisprudenceAdminService';

const JurisprudenceImportPage: React.FC = () => {
  const [coreDbFile, setCoreDbFile] = useState<File | null>(null);
  const [vectorsDbFile, setVectorsDbFile] = useState<File | null>(null);
  const [importType, setImportType] = useState<'jurisprudence' | 'glossary' | 'both'>('both');
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importMessage, setImportMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  const [stats, setStats] = useState<Statistics | null>(null);
  const [logs, setLogs] = useState<ImportLog[]>([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [logFilter, setLogFilter] = useState<string>('');

  useEffect(() => {
    loadStatistics();
    loadLogs();
  }, []);

  const loadStatistics = async () => {
    try {
      setLoadingStats(true);
      const data = await getStatistics();
      setStats(data);
    } catch (error: any) {
      console.error('Erro ao carregar estatísticas:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const loadLogs = async () => {
    try {
      setLoadingLogs(true);
      const data = await getImportLogs(0, 20, logFilter || undefined);
      setLogs(data.logs);
    } catch (error: any) {
      console.error('Erro ao carregar logs:', error);
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleCoreDbSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setCoreDbFile(event.target.files[0]);
    }
  };

  const handleVectorsDbSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setVectorsDbFile(event.target.files[0]);
    }
  };

  const handleImport = async () => {
    if (!coreDbFile || !vectorsDbFile) {
      setImportMessage({ type: 'error', text: 'Selecione os dois arquivos SQLite' });
      return;
    }

    try {
      setImporting(true);
      setImportProgress(0);
      setImportMessage(null);

      // Simular progresso
      const progressInterval = setInterval(() => {
        setImportProgress((prev) => Math.min(prev + 10, 90));
      }, 500);

      const result = await importPandoraData(coreDbFile, vectorsDbFile, importType);

      clearInterval(progressInterval);
      setImportProgress(100);

      if (result.success) {
        setImportMessage({
          type: 'success',
          text: `Importação concluída! ${(result.data as any).imported || 0} novos registros, ${(result.data as any).updated || 0} atualizados.`,
        });
        
        // Recarregar stats e logs
        loadStatistics();
        loadLogs();
        
        // Limpar arquivos
        setCoreDbFile(null);
        setVectorsDbFile(null);
      } else {
        setImportMessage({
          type: 'error',
          text: result.message || 'Erro na importação',
        });
      }
    } catch (error: any) {
      console.error('Erro na importação:', error);
      
      let errorMessage = 'Erro ao importar dados';
      
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setImportMessage({
        type: 'error',
        text: errorMessage,
      });
    } finally {
      setImporting(false);
      setImportProgress(0);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'processing':
        return 'info';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string): React.ReactElement | undefined => {
    switch (status) {
      case 'completed':
        return <CheckCircle fontSize="small" />;
      case 'processing':
        return <Pending fontSize="small" />;
      case 'failed':
        return <Error fontSize="small" />;
      default:
        return undefined;
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          Importação de Jurisprudências
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Importe dados validados do Pandora para o sistema de produção
        </Typography>
      </Box>

      {/* Estatísticas */}
      {loadingStats ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : stats ? (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  Total de Jurisprudências
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {stats.jurisprudence.total}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  Validadas
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                  {stats.jurisprudence.validated}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  Pendentes
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                  {stats.jurisprudence.pending}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  Glossário
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                  {stats.glossary.total}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : null}

      {/* Upload de Arquivos */}
      <Paper sx={{ p: 4, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
          <CloudUpload sx={{ mr: 1, verticalAlign: 'middle' }} />
          Upload de Arquivos Pandora
        </Typography>

        {importMessage && (
          <Alert severity={importMessage.type} sx={{ mb: 3 }} onClose={() => setImportMessage(null)}>
            {importMessage.text}
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                textAlign: 'center',
                border: coreDbFile ? '2px solid' : '2px dashed',
                borderColor: coreDbFile ? 'success.main' : 'grey.300',
                cursor: 'pointer',
                '&:hover': { borderColor: 'primary.main' },
              }}
            >
              <input
                accept=".db"
                style={{ display: 'none' }}
                id="core-db-file"
                type="file"
                onChange={handleCoreDbSelect}
              />
              <label htmlFor="core-db-file" style={{ cursor: 'pointer', width: '100%', display: 'block' }}>
                <CloudUpload sx={{ fontSize: 48, color: coreDbFile ? 'success.main' : 'grey.400', mb: 2 }} />
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {coreDbFile ? coreDbFile.name : 'pandora_core.db'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {coreDbFile ? `${(coreDbFile.size / 1024 / 1024).toFixed(2)} MB` : 'Clique para selecionar'}
                </Typography>
              </label>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                textAlign: 'center',
                border: vectorsDbFile ? '2px solid' : '2px dashed',
                borderColor: vectorsDbFile ? 'success.main' : 'grey.300',
                cursor: 'pointer',
                '&:hover': { borderColor: 'primary.main' },
              }}
            >
              <input
                accept=".db"
                style={{ display: 'none' }}
                id="vectors-db-file"
                type="file"
                onChange={handleVectorsDbSelect}
              />
              <label htmlFor="vectors-db-file" style={{ cursor: 'pointer', width: '100%', display: 'block' }}>
                <CloudUpload sx={{ fontSize: 48, color: vectorsDbFile ? 'success.main' : 'grey.400', mb: 2 }} />
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {vectorsDbFile ? vectorsDbFile.name : 'pandora_vectors.db'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {vectorsDbFile ? `${(vectorsDbFile.size / 1024 / 1024).toFixed(2)} MB` : 'Clique para selecionar'}
                </Typography>
              </label>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Tipo de Importação</InputLabel>
              <Select
                value={importType}
                label="Tipo de Importação"
                onChange={(e) => setImportType(e.target.value as any)}
              >
                <MenuItem value="both">Jurisprudências + Glossário</MenuItem>
                <MenuItem value="jurisprudence">Apenas Jurisprudências</MenuItem>
                <MenuItem value="glossary">Apenas Glossário</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handleImport}
              disabled={!coreDbFile || !vectorsDbFile || importing}
              startIcon={importing ? <CircularProgress size={20} /> : <CloudUpload />}
            >
              {importing ? 'Importando...' : 'Iniciar Importação'}
            </Button>
          </Grid>

          {importing && (
            <Grid item xs={12}>
              <LinearProgress variant="determinate" value={importProgress} sx={{ height: 8, borderRadius: 4 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                {importProgress}% concluído
              </Typography>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Logs de Importação */}
      <Paper sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            <Assessment sx={{ mr: 1, verticalAlign: 'middle' }} />
            Histórico de Importações
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Refresh />}
            onClick={loadLogs}
            disabled={loadingLogs}
          >
            Atualizar
          </Button>
        </Box>

        <FormControl sx={{ mb: 3, minWidth: 200 }}>
          <InputLabel>Filtrar por Status</InputLabel>
          <Select
            value={logFilter}
            label="Filtrar por Status"
            onChange={(e) => {
              setLogFilter(e.target.value);
              loadLogs();
            }}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="completed">Concluídos</MenuItem>
            <MenuItem value="processing">Em Processamento</MenuItem>
            <MenuItem value="failed">Falhados</MenuItem>
          </Select>
        </FormControl>

        {loadingLogs ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : logs.length === 0 ? (
          <Alert severity="info">Nenhum log de importação encontrado</Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Batch ID</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Importados</TableCell>
                  <TableCell align="right">Atualizados</TableCell>
                  <TableCell align="right">Falhas</TableCell>
                  <TableCell align="right">Taxa Sucesso</TableCell>
                  <TableCell>Data</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {log.batch_id}
                      </Typography>
                    </TableCell>
                    <TableCell>{log.import_type}</TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(log.status)}
                        label={log.status}
                        color={getStatusColor(log.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">{log.total_records}</TableCell>
                    <TableCell align="right">{log.imported_records}</TableCell>
                    <TableCell align="right">{log.updated_records}</TableCell>
                    <TableCell align="right">{log.failed_records}</TableCell>
                    <TableCell align="right">
                      {log.success_rate ? `${log.success_rate.toFixed(1)}%` : '-'}
                    </TableCell>
                    <TableCell>
                      {log.started_at ? new Date(log.started_at).toLocaleString('pt-BR') : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Container>
  );
};

export default JurisprudenceImportPage;

